import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { formatAmount, formatEventDate } from "@/lib/format";
import { ArrowLeft, Copy, Gift, ScanLine } from "lucide-react";

export default function OrgVoucherBatchDetailPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    api.get(`/organizer/voucher-batches/${id}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [id]);

  const copy = (txt, k) => {
    navigator.clipboard?.writeText(txt);
    setCopied(k);
    setTimeout(() => setCopied(null), 1800);
  };

  if (loading) return <div className="z-container" style={{ textAlign: "center" }}><span className="z-loader z-loader-dark" /></div>;
  if (!data) return <div className="z-container">Lot introuvable.</div>;

  const b = data.batch;
  return (
    <div className="z-container" style={{ maxWidth: 800 }} data-testid="batch-detail-page">
      <Link to="/org/vouchers" className="z-link" style={{ display: "inline-flex", alignItems: "center", gap: 4, marginBottom: 14, fontSize: 14 }}>
        <ArrowLeft size={14} /> Mes lots
      </Link>
      <h1 className="z-page-title">{b.title}</h1>
      <p className="z-page-sub">
        {b.status === "pending" && <span className="z-badge" style={{ marginRight: 8 }}>En attente de validation</span>}
        {b.type === "amount" ? `💰 ${formatAmount(b.amount, b.currency)} par bon` : `🎁 ${b.items.length} articles par bon`}
        {b.expires_at && ` · Expire le ${formatEventDate(b.expires_at)}`}
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 12, marginBottom: 18 }}>
        <div className="z-kpi"><div className="z-kpi-label">Total</div><div className="z-kpi-value">{b.quantity}</div></div>
        <div className="z-kpi"><div className="z-kpi-label">Réclamés</div><div className="z-kpi-value">{b.claimed}</div></div>
        <div className="z-kpi"><div className="z-kpi-label">Épuisés</div><div className="z-kpi-value">{b.fully_used}</div></div>
      </div>

      <Link to="/org/voucher-scan" className="z-btn z-btn-primary" style={{ marginBottom: 14 }} data-testid="link-scan"><ScanLine size={16} /> Scanner un bon</Link>

      <h3 style={{ fontSize: 16, fontWeight: 700, margin: "16px 0 10px" }}>Codes ({data.vouchers.length})</h3>
      <div style={{ display: "grid", gap: 8 }}>
        {data.vouchers.map((v) => {
          const status = v.status === "active" && (v.owner_id || v.owner_email) ? "claimed" : v.status === "active" ? "available" : v.status;
          const labels = { available: "Disponible", claimed: "Réclamé", fully_used: "Épuisé", expired: "Expiré" };
          const colors = { available: "z-badge", claimed: "z-badge z-badge-teal", fully_used: "z-badge z-badge-success", expired: "z-badge" };
          return (
            <div key={v.id} className="z-card" style={{ padding: 12, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }} data-testid={`voucher-row-${v.id}`}>
              <code style={{ fontFamily: "monospace", fontSize: 14, fontWeight: 700, color: "var(--z-teal)" }}>{v.code}</code>
              <span className={colors[status]}>{labels[status]}</span>
              {v.owner_name && <span style={{ fontSize: 13, color: "var(--z-muted)" }}>{v.owner_name} ({v.owner_email})</span>}
              {v.type === "amount" && (
                <span style={{ fontSize: 13, color: "var(--z-text)", marginLeft: "auto" }}>
                  {formatAmount(v.remaining_amount, v.currency)} / {formatAmount(v.initial_amount, v.currency)}
                </span>
              )}
              <button onClick={() => copy(v.code, v.id)} className="z-btn z-btn-ghost z-btn-sm" data-testid={`copy-code-${v.id}`}>
                <Copy size={14} /> {copied === v.id ? "Copié" : "Copier"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
